import { ButtonLoading } from 'components/Buttons'
import { Modal } from 'components/Modal'
import { usePlace } from 'store/usePlace'
import FormattedMessage from 'utils/FormattedMessage'
import { needsDarkText } from 'utils/needsDarkText'

const IOS_SHARE_ICON = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    className='inline-block w-5 h-5 align-text-bottom text-[#007AFF]'
    aria-hidden='true'
  >
    <path d='M8 10H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-3' />
    <polyline points='12 2 12 15' />
    <polyline points='9 5 12 2 15 5' />
  </svg>
)

const IOS_ADD_ICON = (
  <span className='inline-flex items-center justify-center w-5 h-5 border border-gray-600 rounded text-gray-600 text-xs font-bold align-text-bottom'>
    +
  </span>
)

const IOS_INSTALL_DISMISSED_KEY = 'ios-install-dismissed'

interface IOSInstallModalProps {
  open: boolean
  onDismiss: () => void
}

export const IOSInstallModal = ({ open, onDismiss }: IOSInstallModalProps) => {
  const { place } = usePlace()
  const buttonColor = place?.customisation?.buttonColor ?? '#7669ff'
  const bulletTextColor = needsDarkText(buttonColor) ? '#111827' : '#ffffff'

  const handleNeverShow = () => {
    localStorage.setItem(IOS_INSTALL_DISMISSED_KEY, '1')
    onDismiss()
  }

  return (
    <Modal open={open} closeIcon={true} onClose={onDismiss}>
      <div className='flex flex-col items-center bg-white p-5 w-full'>
        <img
          className='w-[80px] rounded-2xl mt-6 mb-5'
          src='/icons/apple-touch-icon.png'
          alt='2Meters app icon'
        />
        <h1 className='text-gray-700 text-xl font-bold text-center'>
          <FormattedMessage id='ios.install.banner.title' />
        </h1>
        <p className='text-sm text-gray-600 text-center my-3 max-w-[85%]'>
          <FormattedMessage id='ios.install.banner.body' />
        </p>

        <ol className='text-sm text-gray-700 text-left w-full max-w-[85%] space-y-3 my-3 list-none'>
          <li className='flex items-start gap-2'>
            <span
              className='flex-shrink-0 w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold mt-0.5'
              style={{ backgroundColor: buttonColor, color: bulletTextColor }}
            >
              1
            </span>
            <div>
              <span>
                <FormattedMessage id='ios.install.banner.step1' /> {IOS_SHARE_ICON}
              </span>
              <p className='text-xs text-gray-400 mt-0.5'>
                <FormattedMessage id='ios.install.banner.step1.hint' />
              </p>
            </div>
          </li>
          <li className='flex items-start gap-2'>
            <span
              className='flex-shrink-0 w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold mt-0.5'
              style={{ backgroundColor: buttonColor, color: bulletTextColor }}
            >
              2
            </span>
            <div>
              <span>
                <FormattedMessage id='ios.install.banner.step2' /> {IOS_ADD_ICON}
              </span>
              <p className='text-xs text-gray-400 mt-0.5'>
                <FormattedMessage id='ios.install.banner.step2.hint' />
              </p>
            </div>
          </li>
          <li className='flex items-start gap-2'>
            <span
              className='flex-shrink-0 w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold mt-0.5'
              style={{ backgroundColor: buttonColor, color: bulletTextColor }}
            >
              3
            </span>
            <div>
              <span>
                <FormattedMessage id='ios.install.banner.step3' />
              </span>
              <p className='text-xs text-gray-400 mt-0.5'>
                <FormattedMessage id='ios.install.banner.step3.hint' />
              </p>
            </div>
          </li>
        </ol>

        <ButtonLoading onClick={onDismiss} style='w-full mt-4 !mb-0'>
          <FormattedMessage id='ios.install.banner.dismiss' />
        </ButtonLoading>
        <button
          onClick={handleNeverShow}
          className='text-xs text-gray-400 mt-3 mb-2 underline-offset-2 hover:underline'
        >
          <FormattedMessage id='ios.install.banner.never' />
        </button>
      </div>
    </Modal>
  )
}

export { IOS_INSTALL_DISMISSED_KEY }
